import { Table } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { useAuth } from '../../../context/auth-context'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useDateTime } from '../../../shared/useDateTime'
import { Country, ReportFragment, ReportStatus } from '../../../types'
import { humanFileSize } from './utils'

type TargetReportsTableProps = {
  tableData: ReportFragment[]
  companyName: string
  country: Country
  setGenerationInProgress: (value: boolean) => void
}

export const CompanySettingsTargetReportsTable: FC<TargetReportsTableProps> = ({
  tableData,
  companyName,
  country,
  setGenerationInProgress,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { formatDateTimeShort } = useDateTime(user)
  const { tableActionButtonsRowWidth } = tableActionButtons()

  useEffect(() => {
    if (
      !tableData
        .filter((el) => el.companyName === companyName && el.country === country)
        .some((el) => el.status === ReportStatus.Processing)
    ) {
      setGenerationInProgress(false)
    } else {
      setGenerationInProgress(true)
    }
  }, [companyName, country, setGenerationInProgress, tableData])

  const columns: ColumnProps<ReportFragment>[] = [
    {
      key: 'fileName',
      title: t('companySettingsView.targetReportsTab.table.filename'),
      className: 'minWidth-column-xl textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.filename.localeCompare(reportB.filename)
      },
      render: (rowData: ReportFragment) => <>{rowData.filename}</>,
    },
    {
      key: 'creator',
      title: t('companySettingsView.targetReportsTab.table.creator'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        const nameA = computeFullname(
          user.nameDisplayPreference,
          reportA.creator ? reportA.creator.firstName : undefined,
          reportA.creator ? reportA.creator.lastName : undefined,
        )
        const nameB = computeFullname(
          user.nameDisplayPreference,
          reportB.creator ? reportB.creator.firstName : undefined,
          reportB.creator ? reportB.creator.lastName : undefined,
        )
        return nameA.localeCompare(nameB)
      },
      render: (rowData: ReportFragment) => (
        <>
          {computeFullname(
            user.nameDisplayPreference,
            rowData.creator ? rowData.creator.firstName : undefined,
            rowData.creator ? rowData.creator.lastName : undefined,
          )}
        </>
      ),
    },
    {
      key: 'date',
      title: t('companySettingsView.targetReportsTab.table.date'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return new Date(reportA.date).getTime() - new Date(reportB.date).getTime()
      },
      render: (rowData: ReportFragment) => <>{formatDateTimeShort(rowData.date.toString())}</>,
    },
    {
      key: 'size',
      title: t('companySettingsView.targetReportsTab.table.size'),
      className: 'minWidth-column-s textAlign-left',
      sorter: (reportA: ReportFragment, reportB: ReportFragment) => {
        return reportA.size - reportB.size
      },
      render: (rowData: ReportFragment) => <>{rowData.size === 0 ? '-' : humanFileSize(rowData.size)}</>,
    },
    {
      key: 'actions',
      title: t('companySettingsView.targetReportsTab.table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(1),
      render: (rowData: ReportFragment) => (
        <PerTableActionButtons
          variant="pastel"
          buttons={[
            {
              icon: 'download_fill',
              color: 'primary',
              asLink: rowData.status === ReportStatus.Processed ? rowData.downloadLink : undefined,
              tooltip:
                rowData.status === ReportStatus.Processing
                  ? t('reports.table.processingTooltip')
                  : rowData.status === ReportStatus.Error
                  ? t('reports.table.errorTooltip')
                  : t('reports.table.downloadTooltip'),
              disabled: rowData.status !== ReportStatus.Processed,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Table
      className="light small viewWithTabsAndInfoBox scroll-compensation"
      dataSource={tableData ? tableData.map((data) => ({ ...data, key: data.id })) : []}
      columns={columns}
      pagination={false}
      scroll={{ y: true }}
      locale={{
        emptyText: <PerEmpty icon="noReport_stroke" heading={t('companySettingsView.targetReportsTab.table.empty')} />,
      }}
    />
  )
}
